import type { ComponentReview } from './personalMessagesApi';
import type { CaptureDiagnostic } from './captureDiagnostics';
import type { TakeStyle } from './prompts';
import type { TakeStats } from './recorder';

export type CaptureClipKey = 'intro' | 'name' | 'goodnight';

export interface CaptureQualityReport {
  durationSeconds: number;
  loudnessDb: number;
  signalQuality?: string;
  clipping?: string;
  clippingPercent?: number;
  silencePercent?: number;
  snrDb?: number;
  sampleRate?: number;
}

export interface CaptureSessionConfig {
  captureVersion: number;
  qaVersion: string;
  micCheck: {
    roomToneS: number;
    speechS: number;
    sentence: string;
    snrPassDb: number;
    snrWarnDb: number;
    peakMaxDb: number;
  };
  takePrompts: Array<{
    style: TakeStyle;
    title: string;
    coaching: string;
    text: string;
    minS: number;
    maxS: number;
  }>;
  verbatimPrompts: Array<{
    key: CaptureClipKey;
    title: string;
    template: string;
    coaching: string;
  }>;
}

export interface VoiceCaptureTarget {
  kind: 'voice';
  voiceId?: string;
}

export interface ListenerCaptureTarget {
  kind: 'listener';
  voiceId?: string;
  listenerId: string;
}

export type CaptureTarget = VoiceCaptureTarget | ListenerCaptureTarget;

export interface CaptureProgress {
  target: CaptureTarget;
  complete: boolean;
  completedTakeIndexes: number[];
  completedClipKeys: CaptureClipKey[];
  missing: string[];
  pendingReviews?: ComponentReview[];
}

export interface CaptureUploadResult {
  componentReview?: ComponentReview;
  accepted: boolean;
  storageConfirmed: boolean;
  qualityReport: CaptureQualityReport;
  rejectionReasons?: string[] | null;
  suggestions?: string[] | null;
}

export interface CaptureTake {
  sampleIndex: number;
  style: TakeStyle;
  stats: TakeStats;
  voiceLabel?: string;
  forced?: boolean;
}

export interface CaptureListenerClip {
  clipKey: CaptureClipKey;
  forced?: boolean;
}

export interface CaptureClient {
  emitDiagnostic?(event: CaptureDiagnostic): Promise<void> | void;
  readonly requiresComponentReview?: boolean;
  loadReviewAudio?(target: CaptureTarget, receipt: ComponentReview): Promise<Blob>;
  approveRecording?(target: CaptureTarget, receipt: ComponentReview, requestId: string): Promise<ComponentReview>;
  readonly draftScopeKey: string;
  readonly voiceTargetRequiresId: boolean;
  fetchSessionConfig(): Promise<CaptureSessionConfig | null>;
  ensureVoice(label: string): Promise<VoiceCaptureTarget>;
  fetchProgress(target: CaptureTarget): Promise<CaptureProgress>;
  uploadTake(
    target: CaptureTarget,
    take: CaptureTake,
    blob: Blob,
  ): Promise<CaptureUploadResult>;
  uploadListenerClip(
    target: CaptureTarget,
    clip: CaptureListenerClip,
    blob: Blob,
  ): Promise<CaptureUploadResult>;
  complete(target: CaptureTarget): Promise<void>;
}

export function captureTargetsEqual(
  left: CaptureTarget,
  right: CaptureTarget,
): boolean {
  if (left.kind !== right.kind || left.voiceId !== right.voiceId) return false;
  if (left.kind === 'listener' && right.kind === 'listener') {
    return left.listenerId === right.listenerId;
  }
  return true;
}
